import React, { useCallback, useMemo } from 'react';
import ModuleSettingsPanel from '../../features/editCourse/components/ModuleSettingsPanel';
import CourseSettingsPanel from '../../features/editCourse/components/CourseSettingsPanel';
import QuizSettingsPanel from '../../features/editCourse/components/QuizSettingsPanel';
import { useSelectedModuleForSettings, useSetSelectedModuleForSettings } from '../../features/editCourse/hooks';
import { useCourse, useUpdateCourse, useToggleCourseDraft, useUpdateQuizSettings } from '../../features/course/hooks';
import { useCurrentPageId } from '../../features/viewCourse/hooks';
import { useSetMenuEditRightToggle, useEditRightPanelMode, useSetEditRightPanelMode } from '../../features/global/hooks';
import { useUpdateSectionMutation } from '../../features/sections/sectionsApi';

const EditCourseRightPanel = ({ onClose: onCloseProp }) => {
  const courseData = useCourse();
  const pageId = useCurrentPageId();

  const editRightPanelMode = useEditRightPanelMode();
  const setEditRightPanelMode = useSetEditRightPanelMode();
  const selectedModule = useSelectedModuleForSettings();
  const setSelectedModule = useSetSelectedModuleForSettings();
  const setMenuEditRightToggle = useSetMenuEditRightToggle();

  // Course action hooks
  const updateCourse = useUpdateCourse();
  const toggleCourseDraft = useToggleCourseDraft();
  const updateQuizSettings = useUpdateQuizSettings();
  const [updateSectionTrigger] = useUpdateSectionMutation();

  // Derive quiz info from course data
  const currentSection = useMemo(() => {
    if (!courseData?.toc || !pageId) return null;
    for (const section of courseData.toc) {
      const page = section.pages?.find((p) => p.id === pageId);
      if (page) return section;
    }
    return null;
  }, [courseData, pageId]);

  const isQuizPage = useMemo(() => {
    return courseData?.settings?.quiz?.enabled && currentSection?.type === 'quiz';
  }, [courseData, currentSection]);

  const quizSettings = useMemo(() => {
    if (!isQuizPage || !pageId) return null;
    return courseData?.settings?.quiz?.pages?.[pageId] || {};
  }, [isQuizPage, pageId, courseData]);

  const defaultClose = useCallback(() => {
    setSelectedModule(null);
    setEditRightPanelMode('module');
    setMenuEditRightToggle(false);
  }, [setSelectedModule, setEditRightPanelMode, setMenuEditRightToggle]);

  const closePanel = onCloseProp || defaultClose;

  // Course settings handlers
  const handleSaveCourseSettings = useCallback(async ({ courseData: data, sectionUpdates = [] }) => {
    try {
      await updateCourse({ courseData: data });
      await Promise.all(
        sectionUpdates.map((sectionData) => updateSectionTrigger({ sectionData }).unwrap())
      );
      closePanel();
    } catch (error) {
      console.error('Failed to save course settings:', error);
    }
  }, [updateCourse, updateSectionTrigger, closePanel]);

  // Quiz settings handlers
  const handleSaveQuizSettings = useCallback(async (settings) => {
    try {
      await updateQuizSettings({ course: courseData, pageId, settings });
      closePanel();
    } catch (error) {
      console.error('Failed to save quiz settings:', error);
    }
  }, [updateQuizSettings, courseData, pageId, closePanel]);

  if (editRightPanelMode === 'course' && courseData) {
    return (
      <CourseSettingsPanel
        course={courseData}
        onSave={handleSaveCourseSettings}
        onClose={closePanel}
        onToggleDraft={toggleCourseDraft}
      />
    );
  }

  if (editRightPanelMode === 'quiz' && isQuizPage) {
    return (
      <QuizSettingsPanel
        quizSettings={quizSettings}
        quizName={currentSection?.name}
        onSave={handleSaveQuizSettings}
        onClose={closePanel}
      />
    );
  }

  return (
    <ModuleSettingsPanel
      module={selectedModule}
      onClose={closePanel}
    />
  );
};

export default EditCourseRightPanel;
